import { useEffect, useMemo, useState } from "react";
import { TrendingUp, BarChart3, CheckCircle2, Lock, Info, ScrollText, Filter } from "lucide-react";
import Header from "@/components/Header";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const INR = (n) =>
  `Rs. ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function LevelIncome() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [fId, setFId] = useState("");
  const [fName, setFName] = useState("");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [fLevel, setFLevel] = useState("All");
  const [fRank, setFRank] = useState("All");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/level-income");
        setData(data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
    (async () => {
      try {
        const { data } = await api.get("/level-income/history");
        setHistory(data.items || []);
      } catch (e) { console.error("Failed to load level income history:", e); }
      finally { setHistoryLoading(false); }
    })();
  }, []);

  const uniqueRanks = useMemo(() => {
    const s = new Set(history.map((h) => h.rank));
    return ["All", ...Array.from(s)];
  }, [history]);

  const filtered = useMemo(() => {
    const idQ = fId.trim().toLowerCase();
    const nameQ = fName.trim().toLowerCase();
    const fromT = fFrom ? new Date(fFrom).getTime() : null;
    const toT = fTo ? new Date(fTo).getTime() + 24 * 3600 * 1000 - 1 : null;
    return history.filter((h) => {
      if (idQ && !h.user_code.toLowerCase().includes(idQ)) return false;
      if (nameQ && !(h.name || "").toLowerCase().includes(nameQ)) return false;
      if (fLevel !== "All" && String(h.level) !== fLevel) return false;
      if (fRank !== "All" && h.rank !== fRank) return false;
      if (fromT || toT) {
        const t = h.achievement_date ? new Date(h.achievement_date).getTime() : 0;
        if (fromT && t < fromT) return false;
        if (toT && t > toT) return false;
      }
      return true;
    });
  }, [history, fId, fName, fFrom, fTo, fLevel, fRank]);

  const formatDT = (iso) => {
    if (!iso) return { date: "—", time: "" };
    try {
      const d = new Date(iso);
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      const t = d.toLocaleTimeString("en-IN", {
        hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
      });
      return { date: `${dd}/${mm}/${yyyy}`, time: t };
    } catch { return { date: "—", time: "" }; }
  };

  const rankPill = (rank) => {
    const map = {
      Executive: "bg-blue-100 text-blue-700",
      Promoter: "bg-emerald-100 text-emerald-700",
      Motivator: "bg-purple-100 text-purple-700",
      "Sr. Motivator": "bg-fuchsia-100 text-fuchsia-700",
      "Co-ordinator": "bg-amber-100 text-amber-800",
      "Sr. Co-ordinator": "bg-orange-100 text-orange-800",
      Manager: "bg-red-100 text-red-700",
      Director: "bg-rose-100 text-rose-800",
    };
    return `inline-block text-[11px] font-semibold px-2.5 py-1 rounded-full ${
      map[rank] || "bg-slate-100 text-slate-700"
    }`;
  };

  return (
    <div className="min-h-screen" style={{ background: "#560d0d" }}>
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 pb-16 space-y-5 text-slate-900">

        {/* Title card */}
        <section className="bg-white rounded-2xl shadow-lg p-6">
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}>
            Level Income
          </h1>
          <p className="text-slate-600 mt-1 text-sm">
            Earnings from your team levels (up to 30 levels deep)
          </p>
        </section>

        {loading || !data ? (
          <div className="py-16 flex justify-center">
            <div className="h-8 w-8 rounded-full border-4 border-white/40 border-t-white animate-spin" />
          </div>
        ) : (
          <>
            {/* Total earnings */}
            <section
              data-testid="total-level-income-card"
              className="rounded-2xl p-5 sm:p-6 shadow-xl text-white relative overflow-hidden"
              style={{
                background:
                  "linear-gradient(90deg, #2563eb 0%, #0891b2 100%)",
              }}
            >
              <div className="absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/15 flex items-center justify-center">
                <TrendingUp size={22} />
              </div>
              <div className="relative pr-14">
                <div className="text-sm text-white/85">Total Level Income</div>
                <div className="mt-1 text-4xl font-black tracking-tight"
                     style={{ fontFamily: "Outfit, sans-serif" }}>
                  {INR(data.total_income)}
                </div>
              </div>
            </section>

            {/* Breakdown */}
            <section className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="px-5 pt-5 pb-3">
                <h2 className="font-extrabold text-slate-900 flex items-center gap-2"
                    style={{ fontFamily: "Outfit, sans-serif" }}>
                  <BarChart3 size={18} className="text-blue-600" />
                  Level Income Breakdown
                </h2>
              </div>

              <div className="px-5 pb-4">
                {data.direct_active >= 1 ? (
                  <div
                    data-testid="eligibility-banner"
                    className="rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 text-sm px-4 py-3 text-center"
                  >
                    You are eligible for level income! Total income from downline: {INR(data.total_income)}
                  </div>
                ) : (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm px-4 py-3">
                    You need at least <b>1 active direct referral</b> to start earning Level Income.
                  </div>
                )}
              </div>

              <div data-testid="level-income-tiers" className="px-5 pb-5 space-y-4">
                {data.tiers.map((t) => {
                  const locked = !t.eligible;
                  const range = `${t.from_level}-${t.to_level}`;
                  const expandedTier =
                    t.from_level <= 10; // show per-level chips for first 2 tiers
                  return (
                    <div
                      key={t.label}
                      data-testid={`level-tier-${range}`}
                      className={`rounded-xl border overflow-hidden ${
                        locked
                          ? "border-slate-200 bg-slate-50"
                          : "border-blue-200"
                      }`}
                    >
                      <div
                        className="px-4 py-3 flex items-center justify-between gap-2"
                        style={
                          locked
                            ? {}
                            : {
                                background:
                                  "linear-gradient(90deg, #dbeafe 0%, #cffafe 100%)",
                              }
                        }
                      >
                        <div>
                          <div className="font-extrabold text-slate-900 flex items-center gap-2">
                            <span>Level {range}</span>
                            {locked ? (
                              <Lock size={14} className="text-slate-400" />
                            ) : (
                              <CheckCircle2 size={14} className="text-emerald-600" />
                            )}
                          </div>
                          <div className="text-xs text-slate-600 mt-0.5">
                            ₹{t.per_member} per member · Need {t.required_directs} direct
                            {t.required_directs > 1 ? "s" : ""} active
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-slate-600">
                            {t.active_count} executive{t.active_count === 1 ? "" : "s"}
                          </div>
                          <div className="font-extrabold text-blue-700 text-lg tabular-nums"
                               style={{ fontFamily: "Outfit, sans-serif" }}>
                            Rs. {Number(t.income).toLocaleString("en-IN")}
                          </div>
                        </div>
                      </div>

                      {expandedTier && (
                        <div
                          className="grid grid-cols-5 gap-2 p-3 bg-white"
                          data-testid={`level-tier-cells-${range}`}
                        >
                          {t.per_level.map((pl) => (
                            <div
                              key={pl.level}
                              className="rounded-md border border-slate-200 bg-white text-center py-2 shadow-sm"
                            >
                              <div className="text-xs font-bold text-blue-700">L{pl.level}</div>
                              <div className="text-sm font-extrabold text-slate-900">
                                {pl.active}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="px-5 pb-5">
                <div className="rounded-lg bg-blue-50 border border-blue-200 text-blue-800 text-xs px-4 py-3 flex items-start gap-2">
                  <Info size={14} className="mt-0.5 shrink-0" />
                  <span>
                    Level Income is calculated based on <b>active users only</b>. An active user
                    is someone who has purchased a product to activate their ID.
                  </span>
                </div>
              </div>
            </section>

            {/* Level Income History */}
            <section data-testid="level-income-history-section" className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="px-5 pt-5 pb-4 border-b border-slate-100">
                <h2 className="text-xl font-extrabold flex items-baseline gap-2"
                    style={{ fontFamily: "Outfit, sans-serif" }}>
                  <ScrollText size={18} className="text-red-700 self-center" />
                  Level Income <span className="text-slate-500 font-semibold">History</span>
                </h2>
                <p className="text-xs text-slate-500 mt-1">
                  Active users in your downline generating level income
                </p>
              </div>

              <div className="p-5 border-b border-slate-100">
                <div className="flex items-center gap-2 text-xs text-slate-500 font-semibold uppercase tracking-widest mb-3">
                  <Filter size={13} /> Filters
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-600">User ID</label>
                    <input
                      data-testid="li-filter-id"
                      value={fId}
                      onChange={(e) => setFId(e.target.value)}
                      placeholder="Search User ID..."
                      className="mt-1 w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600">User Name</label>
                    <input
                      data-testid="li-filter-name"
                      value={fName}
                      onChange={(e) => setFName(e.target.value)}
                      placeholder="Search User Name..."
                      className="mt-1 w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600">From Date</label>
                    <input
                      data-testid="li-filter-from"
                      type="date"
                      value={fFrom}
                      onChange={(e) => setFFrom(e.target.value)}
                      className="mt-1 w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600">To Date</label>
                    <input
                      data-testid="li-filter-to"
                      type="date"
                      value={fTo}
                      onChange={(e) => setFTo(e.target.value)}
                      className="mt-1 w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600">Filter by Level (1-30)</label>
                    <select
                      data-testid="li-filter-level"
                      value={fLevel}
                      onChange={(e) => setFLevel(e.target.value)}
                      className="mt-1 w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 outline-none"
                    >
                      <option value="All">All Levels</option>
                      {Array.from({ length: 30 }, (_, i) => i + 1).map((n) => (
                        <option key={n} value={String(n)}>Level {n}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600">Filter by Rank</label>
                    <select
                      data-testid="li-filter-rank"
                      value={fRank}
                      onChange={(e) => setFRank(e.target.value)}
                      className="mt-1 w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 outline-none"
                    >
                      {uniqueRanks.map((r) => (
                        <option key={r} value={r}>{r === "All" ? "All Ranks" : r}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-widest font-bold">
                    <tr>
                      <th className="text-left px-4 py-3">SR. NO.</th>
                      <th className="text-left px-4 py-3">User ID</th>
                      <th className="text-left px-4 py-3">Name</th>
                      <th className="text-left px-4 py-3">Level</th>
                      <th className="text-left px-4 py-3">Rank</th>
                      <th className="text-left px-4 py-3">Amount (Rs.)</th>
                      <th className="text-left px-4 py-3">Achievement Date</th>
                    </tr>
                  </thead>
                  <tbody data-testid="li-history-table" className="divide-y divide-slate-100">
                    {historyLoading ? (
                      <tr><td colSpan={7} className="text-center py-10 text-slate-500">Loading…</td></tr>
                    ) : filtered.length === 0 ? (
                      <tr><td colSpan={7} className="text-center py-10 text-slate-500">
                        No matching level-income entries.
                      </td></tr>
                    ) : filtered.map((h, i) => {
                      const a = formatDT(h.achievement_date);
                      return (
                        <tr key={h.user_code + h.level} className="hover:bg-slate-50">
                          <td className="px-4 py-3 font-semibold text-slate-800">{i + 1}</td>
                          <td className="px-4 py-3 font-mono text-blue-700 font-semibold">{h.user_code}</td>
                          <td className="px-4 py-3 font-semibold">{h.name}</td>
                          <td className="px-4 py-3">
                            <span className="inline-block text-[11px] font-semibold px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                              Level {h.level}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={rankPill(h.rank)}>{h.rank}</span>
                          </td>
                          <td className="px-4 py-3 font-extrabold text-emerald-600 tabular-nums">
                            Rs. {h.amount}
                          </td>
                          <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                            <span className="font-semibold">{a.date}</span>{" "}
                            <span className="text-slate-500 text-xs">{a.time}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 text-xs text-slate-600">
                Showing <b>{filtered.length}</b> of <b>{history.length}</b> entries
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
